import { computed, inject, Injectable, signal } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { UserService } from '../user/user.service';
import { GameProvidersService } from './game-providers.service';
import { GamesService } from './games.service';
import { combineLatest, concatMap, EMPTY, forkJoin, merge, Observable, of, switchMap } from 'rxjs';
import {
  debounceTime,
  delay, filter,
  map,
  tap,
} from 'rxjs/operators';
import { GameCategoryAttrService } from './game-category-attribute.service';
import { FiltersService } from '../filters.service';
import { GameCategory } from './game-category';
import { isArray } from '../../helpers/utils';
import { FavoriteGamesService } from './favorite-games.service';
import { GameCategoryType } from '../../shared/interfaces/game-category-interface';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DOCUMENT } from '@angular/common';
import { SsApiService } from '../api/ss-api.service';
import { FreespinsGamesService } from './freespins-games.service';
import { CmsApiService } from '../api/cms-api.service';
import { isObjectEmpty } from 'ngx-unificator/helpers';

export interface Filters {
  categoriesOptions: Option[],
  providersOptions: Option[],
  isShowFiltersBar: boolean,
  isLoadingProvider: boolean,
  isLoadingGamesList: boolean;
  filters: object,
  filtersSideNav: object,
  availableProviders: number[];
  isShowSearchBar: boolean;
  filtersForm: FormGroup,
  filtersFormSideNav: FormGroup,
  searchForm: FormGroup;
  searchFormMain: FormGroup;
  searchFormSideNav: FormGroup;
  searchFormStr: string;
  gamesLists$: Array<Observable<any>>;
  gamesSideNavList$: Observable<any>;
  currentOffset: number;
  allowLoadMore: boolean;
  countTotalGames: number;
  isClickedOnGlobalSearch: boolean;
  gamesListBySearch: any[];
}

export interface Option {
  value: number;
  label: string;
  group: string;
}

export enum FORM_KEYS {
  CATEGORY = 'category_in[]',
  PROVIDER = 'provider[]',
  ATTRIBUTE = 'attribute[]',
  SEARCH = 'search',
  GAME_ID = 'game_id[]',
  EXTERNAL_ID = 'external_id[]',
}

export enum FILTER_FORM_TYPES {
  FILTERS = 'filters',
  FILTERS_GAMES_SIDE_NAV = 'filtersSideNav'
}

@Injectable({
  providedIn: 'root',
})
export class GameFilterService {
  private _document = inject<Document>(DOCUMENT);


  public user: UserService = inject(UserService);
  private _fb: FormBuilder = inject(FormBuilder);
  private _router: Router = inject(Router);
  private _game: GamesService = inject(GamesService);
  private _category: GameCategoryAttrService = inject(GameCategoryAttrService);
  private _providers: GameProvidersService = inject(GameProvidersService);
  private _filtersService: FiltersService = inject(FiltersService);
  private _ssApi: SsApiService = inject(SsApiService);
  private _favorite: FavoriteGamesService = inject(FavoriteGamesService);
  private _fsGames: FreespinsGamesService = inject(FreespinsGamesService);

  public state = computed(() => this._state());

  public isActiveSideNavSearch = computed(() => {
    return (
      (this._state().filtersSideNav[FORM_KEYS.CATEGORY]?.length || 0) > 0 ||
      (this._state().filtersSideNav[FORM_KEYS.ATTRIBUTE]?.length || 0) > 0 ||
      (this._state().filtersSideNav[FORM_KEYS.PROVIDER]?.length || 0) > 0 ||
      this._state().filtersSideNav[FORM_KEYS.SEARCH]
    );
  });

  public isActiveFiltersSearch = computed(() => {
    return (
      (this._state().filters[FORM_KEYS.CATEGORY]?.length || 0) > 0 ||
      (this._state().filters[FORM_KEYS.ATTRIBUTE]?.length || 0) > 0 ||
      (this._state().filters[FORM_KEYS.PROVIDER]?.length || 0) > 0
    );
  });

  private _isOnlyFsCategory = computed(() => {
    const filters = isObjectEmpty(this._state().filters) ?
      this._state().filtersSideNav : this._state().filters;

    const hasOnlyFreeSpinsCategory =
      filters[FORM_KEYS.CATEGORY]?.length === 1 &&
      filters[FORM_KEYS.CATEGORY][0] === GameCategory.FREE_SPINS_CATEGORY;

    const hasNoAttributes = !filters[FORM_KEYS.ATTRIBUTE]?.length;
    const hasNoProviders = !filters[FORM_KEYS.PROVIDER]?.length;
    const hasNoSearch = !filters[FORM_KEYS.SEARCH];

    return hasOnlyFreeSpinsCategory &&
      hasNoAttributes && hasNoProviders && hasNoSearch;
  });

  private _isHasFsCategory = computed(() => {
    return this._state().filters?.[FORM_KEYS.CATEGORY]
        ?.includes(GameCategory.FREE_SPINS_CATEGORY) ||
      this._state().filtersFormSideNav?.[FORM_KEYS.CATEGORY]
        ?.includes(GameCategory.FREE_SPINS_CATEGORY);
  });

  private _state = signal<Filters>({
    categoriesOptions: null,
    providersOptions: null,
    isShowFiltersBar: false,
    filtersForm: this._fb.group({
      [FORM_KEYS.CATEGORY]: [],
      [FORM_KEYS.PROVIDER]: [],
      [FORM_KEYS.ATTRIBUTE]: [],
    }),
    filtersFormSideNav: this._fb.group({
      [FORM_KEYS.CATEGORY]: [],
      [FORM_KEYS.PROVIDER]: [],
      [FORM_KEYS.ATTRIBUTE]: [],
    }),
    filters: {},
    filtersSideNav: {},
    availableProviders: [],
    isLoadingProvider: false,
    isShowSearchBar: false,
    searchForm: this._fb.group({
      search: [null],
    }),
    searchFormMain: this._fb.group({
      search: [null],
    }),
    searchFormSideNav: this._fb.group({
      search: [null],
    }),
    searchFormStr: '',
    gamesLists$: [],
    gamesSideNavList$: null,
    currentOffset: 0,
    allowLoadMore: false,
    isClickedOnGlobalSearch: false,
    gamesListBySearch: [],
    countTotalGames: null,
    isLoadingGamesList: false,
  });

  constructor() {
    this._initFiltersData();
    this._initValueChangesCategories();
    this._iniValueChangesSearch();
    this._iniValueChangesSearchMain();
    this._initValueChangesSearchSideNav();
  }

  get isGamesRoute(): boolean {
    return this._router.url.includes('games');
  }

  /**
   * Submit games side nav filter
   */
  public onSubmitSideNavFilter() {
    this._updateAllPrepareFiltersStore(
      FILTER_FORM_TYPES.FILTERS_GAMES_SIDE_NAV,
      this._state().filtersFormSideNav,
      this._state().searchFormSideNav);
    this._updateGamesSideNavList();
    this.updateShowFiltersBar(false);
  }

  /**
   * Submit main filters
   */
  public async onSubmitFilter() {
    this._updateAllPrepareFiltersStore(
      FILTER_FORM_TYPES.FILTERS,
      this._state().filtersForm, this._state().searchForm);
    await this.resolveNavigation();
  }

  /**
   * Clear all filters and search
   */
  public async onClearFilter() {
    this._state.update(store => {
      return {
        ...store,
        filters: {},
        filtersSideNav: {},
      };
    });
    this.state().filtersForm.reset({}, { emitEvent: false });
    this.state().filtersFormSideNav.reset({}, { emitEvent: false });
    this.state().searchForm.reset(null, { emitEvent: false });
    this.state().searchFormSideNav.reset(null, { emitEvent: false });
    this._updateAvailableProvidersStore([]);
  }

  /**
   * Remove param search from preset
   */
  public async removeParamSearchFromFilter() {
    this._state.update(store => {
      return {
        ...store,
        filters: {
          ...store.filters,
          [FORM_KEYS.SEARCH]: null,
        },
      };
    });
    this._state().searchForm.get(FORM_KEYS.SEARCH).setValue(null);
    await this.resolveNavigation();
  }

  /**
   * Remove category from filter
   * @param id
   * @param type
   */
  public async removeParamFromFilter(id: number | string, type: FORM_KEYS) {
    const newIds = this.state().filters[type]
      .filter(catId => catId !== id);
    this._state.update(store => {
      return {
        ...store,
        filters: {
          ...store.filters,
          [type]: newIds,
        },
      };
    });
    this._state().filtersForm.controls
      [type === FORM_KEYS.ATTRIBUTE ? FORM_KEYS.CATEGORY : type]
      .setValue(newIds, { emitEvent: false });
    await this.resolveNavigation();

    if (this._state().searchForm.get(FORM_KEYS.SEARCH).value)
      this._updateAvailableProvidersStore([]);
  }

  /**
   * Remove all preset filters
   */
  public async removeAllFilters() {
    await this.onClearFilter();
    await this.resolveNavigation();
  }

  /**
   * Resolve category or provider by slug and
   * return the object with detected param
   * @param slug
   */
  public resolveCategoryOrProvider(slug) {
    if (this._categoriesIdsFromSlugs([slug]).length) {
      return { category: slug };
    } else if (this._providersIdsFromSlugs([slug]).length) {
      return { provider: slug };
    } else if (this._attributesIdsFromSlugs([slug]).length) {
      return { attribute: slug };
    } else {
      return null;
    }
  }

  /**
   * Resolve filters from route params
   * @param params
   */
  public filtersFromRouteParams(params) {

    if ('search' in params) {
      this._state().searchForm.get(FORM_KEYS.SEARCH).setValue(params?.search);
    }

    if ('category' in params) {
      const selectedCategoriesSlugs = isArray(params.category) ?
        params.category : [params.category];

      const updatedCategories = [
        ...this._categoriesIdsFromSlugs(selectedCategoriesSlugs) || [],
      ];

      this._state().filtersForm.get(FORM_KEYS.CATEGORY)
        .setValue(updatedCategories);
    }

    if ('attribute' in params) {
      const selectedAttributesSlugs = isArray(params.attribute) ?
        params.attribute : [params.attribute];
      const selectedCategoriesSlugs = isArray(params.category) ?
        params.category : [params.category];

      const updatedAttributes = [
        ...this._attributesIdsFromSlugs(selectedAttributesSlugs) || [],
      ];

      const updatedCategories = [
        ...this._categoriesIdsFromSlugs(selectedCategoriesSlugs) || [],
      ];

      this._state().filtersForm.get(FORM_KEYS.CATEGORY)
        .setValue([
          ...updatedAttributes,
          ...updatedCategories,
        ]);
    }

    if ('provider' in params) {

      const selectedProvidersSlugs = isArray(params.provider) ?
        params.provider : [params.provider];

      const updatedProviders = [
        ...this._providersIdsFromSlugs(selectedProvidersSlugs) || [],
      ];

      this._state().filtersForm.get(FORM_KEYS.PROVIDER)
        .setValue(updatedProviders);
    }

    this._updateAllPrepareFiltersStore(
      FILTER_FORM_TYPES.FILTERS,
      this._state().filtersForm, this._state().searchForm);
  }

  /**
   * Update filters bar stare
   * @param isShowFiltersBar
   */
  public updateShowFiltersBar(isShowFiltersBar: boolean) {
    this._state.update(store => {
      return {
        ...store,
        isShowFiltersBar,
      };
    });
  }

  /**
   * Update search popup state
   * @param isShowSearchBar
   */
  public updateShowSearchPopup(isShowSearchBar: boolean) {
    this._state.update(store => {
      return {
        ...store,
        isShowSearchBar,
      };
    });
  }

  /**
   * Toggle filters bar
   */
  public toggleFiltersBar() {
    if (!this.state().isShowSearchBar) {
      this.updateShowFiltersBar(true);
    } else {
      if (!this._state().isShowFiltersBar) {
        this.updateShowFiltersBar(true);
      } else {
        this.updateShowFiltersBar(false);
      }
    }
  }

  /**
   * Toggle search popup
   */
  public toggleSearchPopup() {
    if (this._state().isShowSearchBar) {
      this._removeOverlay();
      this.updateShowSearchPopup(false);
      this.updateShowFiltersBar(false);
    } else {
      this._addOverlay();
      this.updateShowSearchPopup(true);
      this.state().searchFormMain.reset();
    }
  }

  /**
   * Increase game list offset
   * @param offset
   */
  public increaseOffset(offset?: number) {
    this._state.update(store => {
      return {
        ...store,
        currentOffset: offset === 0 ?
          offset : store.currentOffset + 1,
      };
    });
  }

  /**
   * Update allow load more state
   * @param allowLoadMore
   */
  public updateLoadMore(allowLoadMore: boolean) {
    this._state.update(store => {
      return {
        ...store,
        allowLoadMore,
      };
    });
  }

  /**
   * Update main game lists observables
   * @param isLoadMore
   */
  public updateGamesList(isLoadMore: boolean) {
    of(EMPTY).pipe(
      tap(() => !isLoadMore ?
        this._clearGamesLists() : EMPTY),
      delay(100),
      tap(() => this._updateGamesLists()),
    ).subscribe();
  }

  /**
   * Add search popup overlay
   * @private
   */
  private _addOverlay() {
    const overlayContainer = this._document.createElement('div');
    overlayContainer.classList.add('search-overlay');
    overlayContainer.style.position = 'fixed';
    overlayContainer.style.top = '0';
    overlayContainer.style.left = '0';
    overlayContainer.style.width = '100%';
    overlayContainer.style.height = '100%';
    overlayContainer.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
    overlayContainer.style.zIndex = '1';
    this._document.body.appendChild(overlayContainer);
  }

  /**
   * Remove search popup overlay
   * @private
   */
  private _removeOverlay() {
    const overlayClass = this._document.querySelector('.search-overlay');
    if (overlayClass) {
      this._document.body.removeChild(overlayClass);
    }
  }

  /**
   * Prepare filters data
   * @param filtersForm
   * @param searchForm
   * @private
   */
  private _prepareDataForFilters(
    filtersForm: FormGroup, searchForm: FormGroup) {
    const categoryIds = filtersForm.get(FORM_KEYS.CATEGORY).value
      || [];
    const attrList = this._category?.attrList || [];

    const newAttributeIds = attrList
      .filter(attr => categoryIds.includes(attr.id))
      .map(attr => attr.id);

    const currentAttributeIds =
      filtersForm.get(FORM_KEYS.ATTRIBUTE).value || [];

    const updatedAttributeIds = currentAttributeIds.filter(id =>
      newAttributeIds.includes(id),
    );

    const mergedAttributeIds =
      Array.from(new Set([...updatedAttributeIds, ...newAttributeIds]));

    const remainingCategoryIds = categoryIds.filter(id =>
      !attrList.some(attr => attr.id === id));

    return {
      [FORM_KEYS.CATEGORY]: remainingCategoryIds,
      [FORM_KEYS.ATTRIBUTE]: mergedAttributeIds,
      [FORM_KEYS.PROVIDER]: filtersForm.get(FORM_KEYS.PROVIDER)?.value || [],
      [FORM_KEYS.SEARCH]: searchForm.get(FORM_KEYS.SEARCH)?.value || null,
    };
  }

  /**
   * Update all store by prepared filters
   * @param type
   * @param filtersForm
   * @param searchForm
   * @private
   */
  private _updateAllPrepareFiltersStore(
    type: FILTER_FORM_TYPES, filtersForm: FormGroup, searchForm: FormGroup) {
    this._state.update(store => {
      return {
        ...store,
        [type]: this._prepareDataForFilters(filtersForm, searchForm),
      };
    });
  }

  /**
   * Check is single filter applied
   * @param field
   * @private
   */
  private _isSingleFilterApplied(field: FORM_KEYS): boolean {

    const isFieldSingle = this._state().filters[field]?.length === 1;

    const isOtherFieldsEmpty = [
      FORM_KEYS.CATEGORY,
      FORM_KEYS.ATTRIBUTE,
      FORM_KEYS.PROVIDER]
      .filter(f => f !== field)
      .every(f => !this._state().filters[f]?.length);

    return isFieldSingle && isOtherFieldsEmpty &&
      !this._state().searchForm.get(FORM_KEYS.SEARCH).value?.search;
  }

  /**
   * Resolve and merge multiple query params
   * @private
   */
  private _resolveMultipleParams(): any {
    const formValue = this._state().filters;
    const changedQueryParams: any = {};

    const resolveFilter = (key: string, slugMethod: (ids: any) => any) => {
      const value = formValue[key] || [];
      return value.length ? slugMethod(value) : null;
    };

    changedQueryParams.category =
      resolveFilter(FORM_KEYS.CATEGORY,
        this._categoriesSlugsFromIds.bind(this));
    changedQueryParams.provider =
      resolveFilter(FORM_KEYS.PROVIDER,
        this._providersSlugsFromIds.bind(this));
    changedQueryParams.attribute =
      resolveFilter(FORM_KEYS.ATTRIBUTE,
        this._attributesSlugsFromIds.bind(this));
    changedQueryParams.search = this._state().filters?.[FORM_KEYS.SEARCH];

    return changedQueryParams;
  }

  /**
   * Navigate to route for filter
   */
  public resolveNavigation() {
    switch (true) {
      case this._isSingleFilterApplied(FORM_KEYS.CATEGORY):
        return this._router.navigate([
          '/', 'games',
          this._categoriesSlugsFromIds(
            this._state().filters[FORM_KEYS.CATEGORY])[0],
        ]);
      case this._isSingleFilterApplied(FORM_KEYS.ATTRIBUTE):
        return this._router.navigate([
          '/', 'games',
          this._attributesSlugsFromIds(
            this._state().filters[FORM_KEYS.ATTRIBUTE])[0],
        ]);
      case this._isSingleFilterApplied(FORM_KEYS.PROVIDER):
        return this._router.navigate([
          '/', 'games',
          this._providersSlugsFromIds(
            this._state().filters[FORM_KEYS.PROVIDER])[0],
        ]);
      default:
        const newQueryParams = this._resolveMultipleParams();
        return this._router.navigate(['/', 'games'], {
          queryParams: newQueryParams,
          queryParamsHandling: 'merge',
        });
    }
  }

  /**
   * Init filters data for checkboxes
   * @private
   */
  private _initFiltersData() {
    combineLatest([
      this._createCategoriesOptions$(),
      this._createProvidersOptions$(),
    ]).subscribe();
  }

  /**
   * Init value changes for checkboxes category / providers
   * @private
   */
  private _initValueChangesCategories() {
    merge(
      this._state().filtersForm.get(FORM_KEYS.CATEGORY).valueChanges,
      this._state().filtersForm.get(FORM_KEYS.PROVIDER).valueChanges,
      this._state().filtersFormSideNav.get(FORM_KEYS.CATEGORY).valueChanges,
      this._state().filtersFormSideNav.get(FORM_KEYS.PROVIDER).valueChanges,
    ).pipe(
      filter(() => !this._state().searchForm.get(FORM_KEYS.SEARCH).value),
      tap(() => {
        this._updateAllPrepareFiltersStore(
          FILTER_FORM_TYPES.FILTERS,
          this._state().filtersForm, this._state().searchForm);
        this._updateAllPrepareFiltersStore(
          FILTER_FORM_TYPES.FILTERS_GAMES_SIDE_NAV,
          this._state().filtersFormSideNav, this._state().searchFormSideNav);
      }),
      debounceTime(1000),
      takeUntilDestroyed(),
      tap(() => {
        this._updateLoadingAvailableProvidersStore(true);
      }),
      switchMap(() => {
        if (this.isActiveFiltersSearch()) {
          return this._getAvailableGamesByProviders(FILTER_FORM_TYPES.FILTERS);
        } else {
          return this._getAvailableGamesByProviders(FILTER_FORM_TYPES.FILTERS_GAMES_SIDE_NAV);
        }
      }),
      tap(({ providerList }) => {
        this._updateLoadingAvailableProvidersStore(false);
        this._updateAvailableProvidersStore(providerList);
      }),
    ).subscribe();
  }

  /**
   * Init value changes for main search input
   * @private
   */
  private _iniValueChangesSearch() {
    this._state().searchForm.valueChanges.pipe(
      takeUntilDestroyed(),
      debounceTime(500),
      tap(async () => {
        await this.onSubmitFilter();
      }),
    ).subscribe();
  }

  /**
   * Init value changes for sidebar search input
   * @private
   */
  private _initValueChangesSearchSideNav() {
    this._state().searchFormSideNav.valueChanges.pipe(
      takeUntilDestroyed(),
      debounceTime(500),
      tap(() => this.onSubmitSideNavFilter()),
    ).subscribe();
  }

  /**
   * Init value changes for main search input
   * @private
   */
  private _iniValueChangesSearchMain() {
    this._state().searchFormMain.valueChanges.pipe(
      takeUntilDestroyed(),
      debounceTime(500),
      tap(() => {
        this._updateGamesListBySearch(null);
        this._updateSearchStringStore(null);
      }),
      filter(() => this._state().searchFormMain.get(FORM_KEYS.SEARCH).value),
      switchMap(({ search }) => {
        this._updateSearchStringStore(search);
        return this._game.list({
          search,
          limit: 18,
        });
      }),
      tap(({ gameList }) => {
        this._updateGamesListBySearch(gameList);
      }),
    ).subscribe();
  }

  /**
   * Store search string
   * @param searchFormStr
   * @private
   */
  private _updateSearchStringStore(searchFormStr: string) {
    this._state.update(store => {
      return {
        ...store,
        searchFormStr,
      };
    });
  }

  /**
   * Update games list by search
   * @param gamesListBySearch
   * @private
   */
  private _updateGamesListBySearch(gamesListBySearch: any[]) {
    this._state.update(store => {
      return {
        ...store,
        gamesListBySearch,
      };
    });
  }

  /**
   * Update available providers by category
   * @param providerList
   * @private
   */
  private _updateAvailableProvidersStore(providerList: any[]) {
    this._state.update(store => {
      return {
        ...store,
        availableProviders: providerList?.map(provider => provider.id),
      };
    });
  }

  /**
   * Update loading state available providers
   * @param isLoadingProvider
   * @private
   */
  private _updateLoadingAvailableProvidersStore(isLoadingProvider: boolean) {
    this._state.update(store => {
      return {
        ...store,
        isLoadingProvider,
      };
    });
  }

  /**
   * Clear games lists
   * @private
   */
  private _clearGamesLists() {
    this._state.update(store => {
      return {
        ...store,
        gamesLists$: null,
      };
    });
  }

  /**
   * Update games lists
   * @private
   */
  private _updateGamesLists() {
    this._state.update(store => {
      return {
        ...store,
        gamesLists$: [
          ...(store.gamesLists$ || []),
          this._getGamesList(this._state().filters),
        ],
      };
    });
  }

  /**
   * Update games list for side nav
   * @private
   */
  private _updateGamesSideNavList() {
    this._state.update(store => {
      return {
        ...store,
        gamesSideNavList$:
          this._getGamesList(this._state().filtersSideNav),
      };
    });
  }

  /**
   * Update loading games state
   * @param isLoadingGamesList
   * @private
   */
  private _updateLoadingGamesList(isLoadingGamesList: boolean) {
    this._state.update(store => {
      return {
        ...store,
        isLoadingGamesList,
      };
    });
  }

  /**
   * Get games list
   * @param filters
   * @private
   */
  private _getGamesList(filters: object) {
    return this._processFilters(filters).pipe(
      map((finalFilters) => this._buildParams(finalFilters)),
      concatMap((params) => this._fetchGames(params)),
    );
  }

  /**
   * Process filters
   * @param filters
   * @private
   */
  private _processFilters(filters: object): Observable<any> {
    const hasFavoriteCategory = filters
      [FORM_KEYS.CATEGORY]?.includes(GameCategory.FAVORITE_ID);
    const hasLastPlayedCategory = filters
      [FORM_KEYS.CATEGORY]?.includes(GameCategory.LAST_PLAYED_ID);

    if (hasFavoriteCategory && hasLastPlayedCategory) {
      return this._processFavoriteAndLastPlayedCategories(filters);
    } else if (hasFavoriteCategory) {
      return this._processFavoriteCategory(filters);
    } else if (hasLastPlayedCategory) {
      return this._processLastPlayedCategory(filters);
    }
    return of(filters);
  }

  /**
   * Process favorite and last played games
   * @param filters
   * @private
   */
  private _processFavoriteAndLastPlayedCategories(filters: any):
    Observable<any> {
    return forkJoin({
      favoriteGameIds: this._getFavoriteGamesIds$(),
      lastPlayedGameIds: this._getLastPlayedGamesIds$(),
    }).pipe(
      map(({ favoriteGameIds, lastPlayedGameIds }) => {
        const updatedFilters = {
          ...filters,
          [FORM_KEYS.GAME_ID]: favoriteGameIds,
          [FORM_KEYS.EXTERNAL_ID]: lastPlayedGameIds,
        };
        updatedFilters[FORM_KEYS.CATEGORY] =
          updatedFilters[FORM_KEYS.CATEGORY].filter(
            (id: number) =>
              id !== GameCategory.FAVORITE_ID &&
              id !== GameCategory.LAST_PLAYED_ID,
          );
        return updatedFilters;
      }),
    );
  }

  /**
   * Process favorite games
   * @param filters
   * @private
   */
  private _processFavoriteCategory(filters: object): Observable<any> {
    return this._getFavoriteGamesIds$().pipe(
      map((favoriteGameIds) => {
        const updatedFilters = {
          ...filters,
          [FORM_KEYS.GAME_ID]: favoriteGameIds,
        };
        updatedFilters[FORM_KEYS.CATEGORY] =
          updatedFilters[FORM_KEYS.CATEGORY].filter(
            (id: number) => id !== GameCategory.FAVORITE_ID,
          );
        return updatedFilters;
      }),
    );
  }

  /**
   * Process last played games
   * @param filters
   * @private
   */
  private _processLastPlayedCategory(filters: any): Observable<any> {
    return this._getLastPlayedGamesIds$().pipe(
      map((lastPlayedGameIds) => {
        const updatedFilters = {
          ...filters,
          [FORM_KEYS.EXTERNAL_ID]: lastPlayedGameIds,
        };
        updatedFilters[FORM_KEYS.CATEGORY] =
          updatedFilters[FORM_KEYS.CATEGORY].filter(
            (id: number) => id !== GameCategory.LAST_PLAYED_ID,
          );
        return updatedFilters;
      }),
    );
  }

  /**
   * Build params for filters
   * @param filters
   * @private
   */
  private _buildParams(filters: object): any {
    return {
      ...Object.fromEntries(
        Object.entries(filters).filter(([key, value]) => value !== null),
      ),
      limit: 42,
      offset: this._state().currentOffset * 42,
    };
  }

  /**
   * Fetch games by params
   * @param params
   * @private
   */
  private _fetchGames(params: any): Observable<any> {
    this._updateLoadingGamesList(true);

    const isEmptyGameId = params?.[FORM_KEYS.GAME_ID]?.length === 0;
    const isEmptyExternalId = params?.[FORM_KEYS.EXTERNAL_ID]?.length === 0;

    if (isEmptyGameId || isEmptyExternalId) {
      return this._fetchEmptyFavoriteLastGames$();
    }

    if (this._isOnlyFsCategory()) {
      return this._fetchFsGames$();
    } else if (this._isHasFsCategory() && !this._isOnlyFsCategory()) {
      return this._fetchFsAndOtherGames$(params);
    } else {
      return this._fetchDefaultGames$(params);
    }
  }

  private _fetchEmptyFavoriteLastGames$(): Observable<any> {
    return of({ gameList: [], countTotal: 0 }).pipe(
      tap((response) => {
        this._updateLoadingGamesList(false);
        this._state.update((store) => ({
          ...store,
          countTotalGames: response?.countTotal,
        }));
      }),
    );
  }

  private _fetchFsGames$(): Observable<any> {
    return this._getFsGames$().pipe(
      tap((response) => {
        this._updateLoadingGamesList(false);
        this._state.update((store) => ({
          ...store,
          countTotalGames: response?.gameList?.length > 0 ?
            response?.gameList?.length : 0,
          allowLoadMore: (response?.gameList?.length % 42) === 0,
        }));
      }),
    );
  }

  private _fetchFsAndOtherGames$(params: any): Observable<any> {
    const fsGames$ = this._getFsGames$();
    const otherParams = {
      ...params,
      [FORM_KEYS.CATEGORY]: params[FORM_KEYS.CATEGORY]
        .filter((id: number) => id !== GameCategory.FREE_SPINS_CATEGORY),
    };
    const otherGames$ = this._game.list(otherParams);

    return combineLatest([fsGames$, otherGames$]).pipe(
      map(([fsGames, otherGames]) => ({
        gameList: [...fsGames.gameList, ...otherGames.gameList],
        countTotal: fsGames?.gameList?.length > 0 ? fsGames?.gameList?.length :
          0 + otherGames.countTotal,
      })),
      tap((response) => {
        this._updateLoadingGamesList(false);
        this._state.update((store) => ({
          ...store,
          countTotalGames: response?.countTotal,
          allowLoadMore: (response?.gameList?.length % 42) === 0,
        }));
      }),
    );
  }

  private _fetchDefaultGames$(params: any): Observable<any> {
    return this._game.list(params).pipe(
      tap((response) => {
        this._updateLoadingGamesList(false);
        this._state.update((store) => ({
          ...store,
          countTotalGames: response?.countTotal,
          allowLoadMore: (response?.gameList?.length % 42) === 0,
        }));
      }),
    );
  }

  private _getFsGames$() {
    return this._fsGames.getMappedGameList();
  }

  /**
   * Get last played ids
   * @private
   */
  private _getLastPlayedGamesIds$() {
    return this._ssApi.playerPlayedGames().pipe(
      map(response => (response || []).map((game: any) => game.identifier)),
    );
  }

  /**
   * Get favorite games ids
   * @private
   */
  private _getFavoriteGamesIds$() {
    return this._favorite.getFavoriteGames();
  }

  /**
   * Merge attr with categories and created options for multiple checkbox
   * @private
   */
  public _createCategoriesOptions$(): Observable<Option[]> {
    return this._category.categoryAttrList$.pipe(
      map(list => this.user.auth
        ? list.categoryList
          .concat(list.attributeList).map((item: GameCategoryType) => {
            return {
              value: item.id,
              label: item.name,
            };
          })
        : this._filtersService.excludeUsedByKey(
          'id',
          [
            GameCategory.LAST_PLAYED_ID,
            GameCategory.FAVORITE_ID,
            GameCategory.FREE_SPINS_CATEGORY,
          ],
          list.categoryList.concat(list.attributeList))
          .map((item: GameCategoryType) => {
            return {
              value: item.id,
              label: item.name,
            };
          }),
      ),
      map((categoriesOptions: any) => {
        this._state.update(store => {
          return {
            ...store,
            categoriesOptions,
          };
        });
        return categoriesOptions;
      }),
    );
  }

  /**
   * Create provider options for multiple checkbox
   * @private
   */
  public _createProvidersOptions$(): Observable<any> {
    return this._providers.list$.pipe(
      map(list => {
        return list.map(item =>
          ({ ...item, value: item.id, label: item.name }));
      }),
      map((list: any[]) => {
        return list.filter(e => e.inFilter);
      }),
      map(list => this._filtersService.sortByProp(list, 'alias')),
      tap(providersOptions => {
        this._state.update(store => {
          return {
            ...store,
            providersOptions,
          };
        });
      }),
    );
  }

  /**
   * Get ids from slugs for categories
   * @param slugs
   */
  private _categoriesIdsFromSlugs(slugs: string[]): number[] {
    return slugs.map(slug => {
      const cat = this._category.categoryList
        .find(category => category.slug === slug);
      return cat ? cat.id : null;
    }).filter(id => !!id) as number[];
  }

  /**
   * Get slugs from ids for categories
   * @param ids
   */
  private _categoriesSlugsFromIds(ids: number[]): string[] {
    if (ids?.length) {
      return ids
        .map(id => this._category.categoryList
          .find(category => category.id === id)?.slug)
        .filter(slug => !!slug);
    } else {
      return [];
    }
  }

  /**
   * Get slugs from ids for providers
   * @param ids
   */
  private _providersSlugsFromIds(ids: number[]): string[] {
    if (ids?.length) {
      return ids.map(id => this._providers.getById(id)?.alias)
        .filter(slug => !!slug);
    } else {
      return [];
    }
  }

  /**
   * Get ids from slugs for providers
   * @param slugs
   */
  private _providersIdsFromSlugs(slugs: string[]): number[] {
    if (slugs) {
      return slugs
        .map(slug => {
          const provider = this._providers.getByAlias(slug);
          return provider ? +provider.id : null;
        })
        .filter(id => !!id);
    }
  }

  /**
   * Get slugs from ids for attributes
   * @param ids
   */
  private _attributesSlugsFromIds(ids: number[]): string[] {
    if (ids) {
      return ids
        .map(id => {
          const attr = this._category.getAttrById(id);
          return attr ? attr.slug : null;
        })
        .filter(slug => !!slug);
    }
  }

  /**
   * Get ids from slugs for categories
   * @param slugs
   */
  private _attributesIdsFromSlugs(slugs: string[]): number[] {
    return slugs
      .map(slug => {
        const attr = this._category.getAttrByAlias(slug);
        return attr ? +attr.id : null;
      })
      .filter(id => !!id) as number[];
  }

  private _getAvailableGamesByProviders(formType: FILTER_FORM_TYPES): Observable<any> {
    const params = {
      [FORM_KEYS.CATEGORY]: [...this._state()?.[formType]?.[FORM_KEYS.CATEGORY]],
      [FORM_KEYS.ATTRIBUTE]: [...this._state()?.[formType]?.[FORM_KEYS.ATTRIBUTE]],
      'extra_fields[]': 'provider',
      limit: 12,
    };

    return params[FORM_KEYS.CATEGORY]
      .includes(GameCategory.FAVORITE_ID || GameCategory.LAST_PLAYED_ID) ?
      of([]) : this._game.list(params);
  }
}
